import { fromEvent, map } from 'rxjs';

export default class Email {
  constructor() {
    this.valueInput = '';
    this.placeholderValue = '';
    this.id = 'email-input';
    this.matchingPattern =
      /^(([^<>()\\[\]\\.,;:\s@"]+(\.[^<>()\\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  }

  /**
   * Sets the value of the input field.
   * If no value is provided, it defaults to an empty string.
   *
   * @param {string} newValue - The new value to set. Defaults to an empty string if falsy.
   */
  set value(newValue) {
    this.valueInput = newValue || '';
  }

  /**
   * Getter for the value property.
   * @returns {string} The current value of the valueInput property.
   */
  get value() {
    return this.valueInput;
  }

  /**
   * Sets the placeholder value for the input component.
   *
   * @param {string} value - The placeholder text to display. If no value is provided, it defaults to an empty string.
   */
  set placeholder(value) {
    this.placeholderValue = value || '';
  }

  /**
   * Gets the placeholder value for the input component.
   * @returns {string} The placeholder text.
   */
  get placeholder() {
    return this.placeholderValue;
  }

  /**
   * Generates the HTML markup for an email input component.
   *
   * @returns {string} The HTML string for the email input component, including a wrapper, label, input field
   * and error message.
   */
  getHtml() {
    return `
      <div class="nhs-c-input__wrapper" data-email-wrapper>
        <label class="nhs-c-input__label nhs-c-label--black-1000-body-small-regular">Email</label>
        <input type="email" class="nhs-c-input__field nhs-u-flex--1-0-auto" placeholder="${this.placeholderValue}" id="${this.id}" data-email-input
          name="email" autocomplete="email" required value="${this.value}" />
        <span data-error-label class="nhs-c-input__label--error">Please enter a valid email address</span>
      </div>
    `;
  }

  /**
   * Creates an observable that listens for 'change' events on the associated DOM node
   * and maps the event to an object containing the email value from the event target.
   *
   * @returns {Observable} An observable emitting an object with the email value.
   */
  getOnChangeEvent$() {
    return fromEvent(this.getNode(), 'change').pipe(map((event) => ({ email: event.target.value })));
  }

  /**
   * Retrieves the DOM element associated with the current instance's ID.
   *
   * @returns {HTMLElement} The DOM element with the specified ID, or null if no element is found.
   */
  getNode() {
    return document.getElementById(this.id);
  }

  /**
   * Retrieves the current value of the input node.
   *
   * @returns {string} The value of the input element.
   */
  getValue() {
    return this.getNode().value;
  }

  /**
   * Retrieves the closest parent element with the attribute `[data-email-wrapper]`
   * relative to the current node.
   *
   * @returns {Element} The closest ancestor element with the `[data-email-wrapper]` attribute.
   */
  getInputWrapper() {
    return this.getNode().closest('[data-email-wrapper]');
  }

  /**
   * Resets the value of the associated node and the internal value property to an empty string.
   * This method is typically used to clear any input or state associated with the component.
   */
  reset() {
    const node = this.getNode();
    node.value = '';
    this.value = '';
  }

  /**
   * Sets the invalid state for the given input node.
   * Adds the 'aria-invalid' attribute to the node and applies an error class
   * to the input wrapper to visually indicate an error state.
   *
   * @param {HTMLElement} node - The input element to mark as invalid.
   */
  setInvalidState(node) {
    const wrapper = this.getInputWrapper();
    node.setAttribute('aria-invalid', true);
    wrapper.classList.add('nhs-c-input__wrapper--error');
  }

  /**
   * Sets the valid state for the input element by removing error-related attributes and classes.
   *
   * @param {HTMLElement} node - The input element to update.
   */
  setValidState(node) {
    const wrapper = this.getInputWrapper();

    node.removeAttribute('aria-invalid');
    wrapper.classList.remove('nhs-c-input__wrapper--error');
  }

  /**
   * Validates the input value of the associated DOM node.
   *
   * - Trims leading and trailing whitespace from the input value.
   * - Replaces multiple consecutive spaces with a single space.
   * - Updates the input value in the UI with the cleaned value.
   * - Checks if the cleaned value matches a predefined pattern.
   * - Sets the input state to valid or invalid based on the validation result.
   *
   * @returns {boolean} `true` if the input value is valid, otherwise `false`.
   */
  validate() {
    const node = this.getNode();

    /* NHS20-1684: we verify that the value taken from the input it's clean,
    in case of spaces before and after the text, also is updated in the UI. */
    const cleanedValue = node.value.trim().replace(/\s+/g, ' ');
    node.value = cleanedValue;
    const isValid = node.value && this.matchingPattern.test(node.value);

    if (isValid) {
      this.setValidState(node);
    } else {
      this.setInvalidState(node);
    }

    return isValid;
  }
}
